import React from 'react';
import {Redirect} from 'react-router';
import {useParams} from 'react-router-dom';

import NoMatch from '../components/NoMatch';
import {entity as entityPage} from '../util/urls';
import {InstrumentedLoader as Loader} from './../components/utility/InstrumentedLoader';
import {useEntityPageData} from './EntityPage/useEntityPageData';
import {EntityRunsContextProvider} from './EntityPage/useEntityRuns';
import ProfilePage from './ProfilePage/ProfilePage';
import {TeamPage} from './Team';
import {InviteOnlyTeam} from './Team/InviteOnlyTeam';
import {
  isFollowingInviteLinkAndToNewUser,
  useRedirectToSignupIfFollowingInviteLinkAndNotLoggedIn,
} from './Team/utils';

interface EntityPageProps {
  tab?: string;
}

const EntityPage = React.memo(({tab}: EntityPageProps) => {
  const {entityName, filePath} = useParams<{
    entityName: string;
    filePath: string;
  }>();

  const {data, error, initialLoading, isViewerLoggedIn, isTeamMemberOrAdmin} =
    useEntityPageData(entityName);

  const {isFollowingInviteLink, inviteIsToNewUser} =
    isFollowingInviteLinkAndToNewUser(entityName, 'Entity Page');

  useRedirectToSignupIfFollowingInviteLinkAndNotLoggedIn({
    isInviteToNewUser: isFollowingInviteLink && inviteIsToNewUser,
    isViewerLoggedIn,
  });

  /**
   * Control checks:
   * 1. Don't render a page until we know what kind of page it is
   * 2. Don't render for missing data
   * 3. If the entity has been claimed, redirect to the claiming entity instead so the original link still works.
   * 4. Split out profiles from team pages
   * 5. Don't allow access to restricted team pages
   */
  // #1
  // TODO: figure out error case
  if (initialLoading || error) {
    return <Loader name="entity-page-loader" />;
  }

  // #2
  // data.available means "no entity exists with this name"
  if (data == null || data.available) {
    return <NoMatch />;
  }

  // #3
  if (data.claimingEntity != null) {
    return <Redirect to={entityPage(data.claimingEntity.name)} />;
  }

  // #4
  if (!data.isTeam) {
    return <ProfilePage filePath={filePath} tab={tab} />;
  }

  // #5
  if (!isTeamMemberOrAdmin) {
    return <InviteOnlyTeam teamName={data.name} />;
  }

  return <TeamPage entityName={entityName} tab={tab} />;
});

const EntityPageDataWrapper = (props: EntityPageProps) => (
  <EntityRunsContextProvider>
    <EntityPage {...props} />
  </EntityRunsContextProvider>
);
export default EntityPageDataWrapper;
